import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { AlertTriangle, CheckCircle2, ArrowRight, Loader2, TrendingUp, TrendingDown, Minus, Plus, X } from "lucide-react";

/**
 * StatementLedgerDiff — month-over-month diff between two statements.
 *
 * Given the current statement id, calls `/api/statements/:id/ledger/diff` which
 * auto-picks the previous statement on the same card. Optionally accepts an
 * explicit `vsId` to compare against a specific other statement.
 *
 * Highlights:
 *   1. Carry-over check — did last month's closing = this month's opening?
 *   2. Key-number deltas — Opening, Closing, Purchases, Payments, Interest, Fees, GST
 *   3. Per-category spend deltas with badges (Added / Removed / Grew / Shrank)
 */
export default function StatementLedgerDiff({ statementId, vsId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!statementId) return;
        setLoading(true);
        setError(null);
        api.get(`/statements/${statementId}/ledger/diff`, { params: vsId ? { vs: vsId } : {} })
            .then((r) => setData(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Failed to load diff"))
            .finally(() => setLoading(false));
    }, [statementId, vsId]);

    if (loading) return <div className="p-6 text-stone-500 flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Building diff…</div>;
    if (error)   return <div className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded" data-testid="diff-error">{error}</div>;
    if (!data)   return null;

    return (
        <div className="space-y-4" data-testid="statement-ledger-diff">
            <div className="flex items-center justify-between">
                <div>
                    <div className="eyebrow">Statement diff</div>
                    {data.previous_id ? (
                        <div className="text-lg font-medium mt-0.5 flex items-center gap-2">
                            <span className="text-stone-600 tabular-nums" data-testid="diff-previous-month">{data.previous_month}</span>
                            <ArrowRight size={14} className="text-stone-400" />
                            <span className="text-stone-900 tabular-nums" data-testid="diff-current-month">{data.current_month}</span>
                            <span className="text-xs text-stone-400 ml-2">· {data.bank_name} · •••• {data.last_4}</span>
                        </div>
                    ) : (
                        <div className="text-sm text-stone-500 mt-0.5">No prior statement to compare against.</div>
                    )}
                </div>
                {onClose && (
                    <button onClick={onClose} className="p-1.5 rounded hover:bg-stone-100 text-stone-500" title="Close" data-testid="diff-close">
                        <X size={14} />
                    </button>
                )}
            </div>

            {!data.previous_id && (
                <div className="p-6 text-center text-stone-500 text-sm border border-dashed border-stone-300 rounded" data-testid="diff-empty">
                    {data.message || "No previous statement on this card to compare against."}
                </div>
            )}
            {data.previous_id && <DiffBody data={data} />}
        </div>
    );
}

function DiffBody({ data }) {
    const carryOk = data.carry_over_ok;
    return (
        <>
            {/* Carry-over check */}
            <div
                data-testid="diff-carry-over"
                className={`px-3 py-3 border rounded-md text-sm ${carryOk ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}
            >
                <div className="flex items-start gap-2">
                    {carryOk ? <CheckCircle2 size={16} className="mt-0.5" /> : <AlertTriangle size={16} className="mt-0.5" />}
                    <div className="flex-1">
                        <div className="font-medium">
                            {carryOk
                                ? "Carry-over is clean."
                                : "Carry-over mismatch detected."}
                        </div>
                        <div className="text-xs mt-0.5 opacity-90">
                            Previous closing (<span className="font-mono-figure">{fmtMoney(data.previous_closing)}</span>)
                            {" "}→{" "}
                            Current opening (<span className="font-mono-figure">{fmtMoney(data.current_opening)}</span>)
                            {" "}· Δ <span className="font-mono-figure">{fmtMoney(data.carry_over_delta)}</span>
                            {!carryOk && " — this usually means a statement is missing between these two, or one of them was uploaded with a wrong balance."}
                        </div>
                    </div>
                </div>
            </div>

            {/* Key-number deltas */}
            <div>
                <div className="eyebrow mb-2">Key numbers</div>
                <div className="border border-stone-200 rounded-md overflow-hidden">
                    <table className="dense-table w-full">
                        <thead>
                            <tr>
                                <th>Metric</th>
                                <th className="text-right">{data.previous_month}</th>
                                <th className="text-right">{data.current_month}</th>
                                <th className="text-right">Δ</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(data.deltas).map(([k, v]) => (
                                <DiffRow key={k} label={PRETTY[k] || k} previous={v.previous} current={v.current} delta={v.delta} testid={`diff-metric-${k}`} />
                            ))}
                            <DiffRow label="Transaction count" previous={data.previous_txn_count} current={data.current_txn_count} delta={data.txn_count_delta} unit="count" testid="diff-metric-txn_count" />
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Category delta */}
            {data.category_delta.length > 0 && (
                <div>
                    <div className="eyebrow mb-2">Category shifts · debits only</div>
                    <div className="border border-stone-200 rounded-md overflow-hidden">
                        <table className="dense-table w-full">
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th className="text-right">{data.previous_month}</th>
                                    <th className="text-right">{data.current_month}</th>
                                    <th className="text-right">Δ</th>
                                    <th className="w-24">State</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.category_delta.map((c) => (
                                    <tr key={c.category} data-testid={`diff-cat-${c.category}`}>
                                        <td>{c.category}</td>
                                        <td className="text-right tabular-nums text-stone-500">{c.previous ? fmtMoney(c.previous) : "—"}</td>
                                        <td className="text-right tabular-nums font-medium">{c.current ? fmtMoney(c.current) : "—"}</td>
                                        <td className={`text-right tabular-nums ${c.delta > 0 ? "text-red-700" : c.delta < 0 ? "text-emerald-700" : "text-stone-400"}`}>
                                            {c.delta > 0 ? "+ " : c.delta < 0 ? "− " : ""}{fmtMoney(Math.abs(c.delta))}
                                        </td>
                                        <td><StateBadge state={c.state} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    );
}

const PRETTY = {
    opening_balance: "Opening Balance",
    closing_balance: "Closing Balance",
    purchases: "Purchases",
    payments: "Payments",
    interest: "Interest",
    late_fee: "Late Fee",
    gst: "GST",
    total_amount_due: "Total Amount Due",
    min_amount_due: "Min Amount Due",
};

function DiffRow({ label, previous, current, delta, unit, testid }) {
    const isMoney = unit !== "count";
    const fmt = (v) => (isMoney ? fmtMoney(v) : String(v ?? 0));
    let tone = "text-stone-400";
    if (delta > 0) tone = "text-red-700";
    else if (delta < 0) tone = "text-emerald-700";
    return (
        <tr data-testid={testid}>
            <td>{label}</td>
            <td className="text-right tabular-nums text-stone-500">{fmt(previous)}</td>
            <td className="text-right tabular-nums font-medium">{fmt(current)}</td>
            <td className={`text-right tabular-nums ${tone}`}>
                {delta > 0 ? "+ " : delta < 0 ? "− " : ""}{isMoney ? fmtMoney(Math.abs(delta)) : Math.abs(delta)}
            </td>
            <td></td>
        </tr>
    );
}

function StateBadge({ state }) {
    const map = {
        added:     { icon: Plus,         cls: "text-blue-800 bg-blue-50 border-blue-200" },
        removed:   { icon: Minus,        cls: "text-stone-700 bg-stone-100 border-stone-200" },
        grew:      { icon: TrendingUp,   cls: "text-red-800 bg-red-50 border-red-200" },
        shrank:    { icon: TrendingDown, cls: "text-emerald-800 bg-emerald-50 border-emerald-200" },
        unchanged: { icon: Minus,        cls: "text-stone-500 bg-stone-50 border-stone-200" },
    };
    const cfg = map[state] || map.unchanged;
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium uppercase tracking-widest ${cfg.cls}`}>
            <Icon size={10} /> {state}
        </span>
    );
}
